/**
 * CoordinateParser - Parse coordinate strings in multiple formats:
 * - Decimal degrees: "40.7128, -74.0060" or "40.7128 -74.0060"
 * - DMS: "40°42'46\"N 74°0'22\"W" or "40°42'46"N 74°0'22"W"
 * - UTM: "18T 583960 4507523"
 *
 * **Validates: Requirements 28.1, 28.2, 28.3, 28.4**
 */

export interface ParsedCoordinate {
  /** [longitude, latitude] in decimal degrees (WGS84) */
  coordinates: [number, number];
  /** The format that was detected */
  format: 'decimal-degrees' | 'dms' | 'utm';
  /** Whether the parse was successful */
  valid: boolean;
}

export interface CoordinateValidationError {
  valid: false;
  error: string;
}

export type CoordinateParseResult = ParsedCoordinate | CoordinateValidationError;

/**
 * Try to parse a coordinate string in any supported format.
 * Returns [longitude, latitude] or null if unparseable.
 *
 * **Validates: Requirements 28.1, 28.2**
 */
export function parseCoordinates(input: string): CoordinateParseResult {
  if (!input || !input.trim()) {
    return { valid: false, error: 'Empty input' };
  }

  const trimmed = input.trim();

  // Try decimal degrees first (most common)
  const dd = parseDecimalDegrees(trimmed);
  if (dd) return dd;

  // Try DMS
  const dms = parseDMS(trimmed);
  if (dms) return dms;

  // Try UTM
  const utm = parseUTM(trimmed);
  if (utm) return utm;

  return {
    valid: false,
    error:
      'Unrecognized coordinate format. Supported: decimal degrees (40.7128, -74.0060), DMS (40°42\'46"N 74°0\'22"W), UTM (18T 583960 4507523)',
  };
}

/**
 * Parse decimal degrees format.
 * Accepts: "lat, lon", "lat lon", "lat,lon"
 * Latitude range: -90 to 90, Longitude range: -180 to 180
 */
function parseDecimalDegrees(input: string): ParsedCoordinate | null {
  // Match patterns like "40.7128, -74.0060" or "40.7128 -74.0060"
  const pattern = /^([+-]?\d+\.?\d*)[,\s]+([+-]?\d+\.?\d*)$/;
  const match = input.match(pattern);
  if (!match) return null;

  const lat = parseFloat(match[1]);
  const lon = parseFloat(match[2]);

  if (isNaN(lat) || isNaN(lon)) return null;
  if (lat < -90 || lat > 90) return null;
  if (lon < -180 || lon > 180) return null;

  return {
    coordinates: [lon, lat],
    format: 'decimal-degrees',
    valid: true,
  };
}

/**
 * Parse DMS (Degrees Minutes Seconds) format.
 * Accepts various DMS notations:
 * - 40°42'46"N 74°0'22"W
 * - 40°42'46.0"N, 74°0'22.0"W
 * - 40d42m46sN 74d0m22sW
 * - N40°42'46" W74°0'22"
 */
function parseDMS(input: string): ParsedCoordinate | null {
  // Normalize unicode quotes and special characters
  let normalized = input
    .replace(/[″"]/g, '"')
    .replace(/[′']/g, "'")
    .replace(/\s*,\s*/g, ' ');

  // Pattern for a single DMS component
  // Matches: 40°42'46"N or N40°42'46" or 40d42m46sN
  // Suffix direction is only captured if immediately after the seconds/quote (no space before it)
  // This prevents "N40°..." prefix from being consumed as suffix of previous component
  const dmsPattern = /([NSEW]?)\s*(\d+)[°d]\s*(\d+)?[′'m]?\s*(\d+\.?\d*)?[″"s]?([NSEW])?/gi;

  const matches = [...normalized.matchAll(dmsPattern)];
  if (matches.length < 2) return null;

  let lat: number | null = null;
  let lon: number | null = null;

  for (const match of matches) {
    const prefix = match[1].toUpperCase();
    const suffix = (match[5] || '').toUpperCase();
    const direction = prefix || suffix;

    const degrees = parseInt(match[2]) || 0;
    const minutes = parseInt(match[3]) || 0;
    const seconds = parseFloat(match[4]) || 0;

    let decimal = degrees + minutes / 60 + seconds / 3600;

    if (direction === 'S' || direction === 'W') {
      decimal = -decimal;
    }

    if (direction === 'N' || direction === 'S') {
      lat = decimal;
    } else if (direction === 'E' || direction === 'W') {
      lon = decimal;
    } else {
      // No direction specified - assume first is lat, second is lon
      if (lat === null) {
        lat = decimal;
      } else if (lon === null) {
        lon = decimal;
      }
    }
  }

  if (lat === null || lon === null) return null;
  if (lat < -90 || lat > 90) return null;
  if (lon < -180 || lon > 180) return null;

  return {
    coordinates: [lon, lat],
    format: 'dms',
    valid: true,
  };
}

/**
 * Parse UTM (Universal Transverse Mercator) format.
 * Accepts: "18T 583960 4507523" or "18 T 583960 4507523"
 *
 * Converts UTM to WGS84 decimal degrees.
 */
function parseUTM(input: string): ParsedCoordinate | null {
  // Pattern: zone + letter + easting + northing
  const pattern = /^(\d{1,2})\s*([C-X])\s+(\d+\.?\d*)\s+(\d+\.?\d*)$/i;
  const match = input.match(pattern);
  if (!match) return null;

  const zone = parseInt(match[1]);
  const letter = match[2].toUpperCase();
  const easting = parseFloat(match[3]);
  const northing = parseFloat(match[4]);

  if (zone < 1 || zone > 60) return null;
  if (easting < 100000 || easting > 999999) return null;
  if (northing < 0 || northing > 10000000) return null;

  // Convert UTM to lat/lon using simplified formula
  const result = utmToLatLon(zone, letter, easting, northing);
  if (!result) return null;

  const [lat, lon] = result;
  if (lat < -90 || lat > 90) return null;
  if (lon < -180 || lon > 180) return null;

  return {
    coordinates: [lon, lat],
    format: 'utm',
    valid: true,
  };
}

/**
 * Convert UTM coordinates to latitude/longitude (WGS84).
 * Uses the Karney method approximation.
 */
function utmToLatLon(
  zone: number,
  letter: string,
  easting: number,
  northing: number
): [number, number] | null {
  const isNorthern = letter >= 'N';

  // WGS84 ellipsoid parameters
  const a = 6378137.0; // semi-major axis
  const f = 1 / 298.257223563; // flattening
  const e = Math.sqrt(2 * f - f * f); // eccentricity
  const e2 = e * e;
  const e4 = e2 * e2;
  const e6 = e4 * e2;

  const k0 = 0.9996; // scale factor
  const x = easting - 500000; // remove false easting
  let y = northing;
  if (!isNorthern) {
    y -= 10000000; // remove false northing for southern hemisphere
  }

  // Footpoint latitude
  const M = y / k0;
  const mu = M / (a * (1 - e2 / 4 - (3 * e4) / 64 - (5 * e6) / 256));

  const e1 = (1 - Math.sqrt(1 - e2)) / (1 + Math.sqrt(1 - e2));
  const e12 = e1 * e1;
  const e13 = e12 * e1;
  const e14 = e13 * e1;

  const phi1 =
    mu +
    ((3 * e1) / 2 - (27 * e13) / 32) * Math.sin(2 * mu) +
    ((21 * e12) / 16 - (55 * e14) / 32) * Math.sin(4 * mu) +
    ((151 * e13) / 96) * Math.sin(6 * mu) +
    ((1097 * e14) / 512) * Math.sin(8 * mu);

  const sinPhi1 = Math.sin(phi1);
  const cosPhi1 = Math.cos(phi1);
  const tanPhi1 = sinPhi1 / cosPhi1;

  const N1 = a / Math.sqrt(1 - e2 * sinPhi1 * sinPhi1);
  const T1 = tanPhi1 * tanPhi1;
  const C1 = (e2 / (1 - e2)) * cosPhi1 * cosPhi1;
  const R1 = (a * (1 - e2)) / Math.pow(1 - e2 * sinPhi1 * sinPhi1, 1.5);
  const D = x / (N1 * k0);

  const D2 = D * D;
  const D3 = D2 * D;
  const D4 = D3 * D;
  const D5 = D4 * D;
  const D6 = D5 * D;

  const lat =
    phi1 -
    ((N1 * tanPhi1) / R1) *
      (D2 / 2 -
        ((5 + 3 * T1 + 10 * C1 - 4 * C1 * C1 - 9 * (e2 / (1 - e2))) * D4) / 24 +
        ((61 + 90 * T1 + 298 * C1 + 45 * T1 * T1 - 252 * (e2 / (1 - e2)) - 3 * C1 * C1) * D6) /
          720);

  const lon0 = ((zone - 1) * 6 - 180 + 3) * (Math.PI / 180); // central meridian
  const lon =
    lon0 +
    (D -
      ((1 + 2 * T1 + C1) * D3) / 6 +
      ((5 - 2 * C1 + 28 * T1 - 3 * C1 * C1 + 8 * (e2 / (1 - e2)) + 24 * T1 * T1) * D5) / 120) /
      cosPhi1;

  const latDeg = lat * (180 / Math.PI);
  const lonDeg = lon * (180 / Math.PI);

  if (isNaN(latDeg) || isNaN(lonDeg)) return null;

  return [latDeg, lonDeg];
}

/**
 * Validate that coordinates are within valid ranges.
 *
 * **Validates: Requirement 28.4**
 */
export function validateCoordinates(lon: number, lat: number): string | null {
  if (lat < -90 || lat > 90) {
    return `Latitude ${lat} is out of range. Must be between -90 and 90.`;
  }
  if (lon < -180 || lon > 180) {
    return `Longitude ${lon} is out of range. Must be between -180 and 180.`;
  }
  return null;
}

/**
 * Check if a string looks like it might be coordinates (for UI hinting).
 */
export function looksLikeCoordinates(input: string): boolean {
  if (!input) return false;
  const trimmed = input.trim();
  // Contains degree symbol or direction letters with numbers
  if (/[°′'″"]/.test(trimmed)) return true;
  // Two numbers separated by comma or space
  if (/^[+-]?\d+\.?\d*[,\s]+[+-]?\d+\.?\d*$/.test(trimmed)) return true;
  // UTM pattern
  if (/^\d{1,2}\s*[C-X]\s+\d+/i.test(trimmed)) return true;
  return false;
}
